import { useEffect, useState } from "react";
import { iif, invokeImmediately } from "../../common/utils/functions";
import { RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { parseDate } from "../../common/utils/TimeUtils";
import { ReviewTasks } from "../../pages/applications/applicationDetail/tabs/dynamic/review/model/ReviewTasks";
import moment from "moment";
import { ApplicationListPageActions } from "../../pages/applications/applicationList/model";
import config from "../../common/config";

export default function useQualarooController({ userDetails }) {
  const applications = useSelector((state: RootState) => state.applicationList.applicationList.value);
  const { data: appsList, currentReviewCycleStartDate } = applications;
  const reviews = useSelector((state: RootState) => state.appReviewTasks.reviews);
  const [qualarooLoaded, setQualarooLoaded] = useState(false);
  const [visible, setVisible] = useState(false);
  const scriptId = "qualaroo-nudge-script";
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(ApplicationListPageActions.fetchAppsIfNotPresent());
  }, []);

  useEffect(() => {
    window._kiq?.push(["set", { visible: visible }]);
  }, [visible]);

  useEffect(() => {
    if (qualarooLoaded) {
      setTimeout(() => window._kiq.push(["maximizeSurvey"]), 1000);
    }
  }, [qualarooLoaded]);

  function showNudgeIfSurveyIsNotSubmittedForCurrentReviewCycle() {
    if (currentReviewCycleStartDate) {
      const surveySubmittedTime = localStorage.getItem("survey-submitted-time");
      return !surveySubmittedTime || (surveySubmittedTime < currentReviewCycleStartDate);
    }
    return false;
  }

  function findReviewById(id: String) {
    return reviews.find((review: any) => review.reviewId === id);
  }

  function getSubmittedTasksCountForUser(review: any) {
    const reviewTasks = review.reviewTasks.value;
    if (reviewTasks && userDetails) {
      return new ReviewTasks(reviewTasks.dataSource).submittedTasksReviewedBy(userDetails.fullName).length;
    }
    return 0;
  }

  useEffect(() => {
    invokeImmediately(function setupAndLoadQualarooSurveyNudge() {
      if (showNudgeIfSurveyIsNotSubmittedForCurrentReviewCycle() && !document.getElementById(scriptId)) {
        const scriptElement = document.createElement("script");
        scriptElement.type = "text/javascript";
        scriptElement.async = true;
        scriptElement.id = scriptId;
        scriptElement.src = "https://cl.qualaroo.com/ki.js/80068/iFR.js";
        document.body.append(scriptElement);
        setQualarooLoaded(true);
        setVisible(true);
      }
    });
  }, [currentReviewCycleStartDate]);

  useEffect(function initUser() {
    if (userDetails) {
      const qualarooSurvey = window._kiq = window._kiq || [];
      qualarooSurvey.push(["identify", userDetails.email]);
      qualarooSurvey.push(["set", {
        "name": userDetails.fullName,
        "employeeId": userDetails.employeeId,
      }]);
    }
  }, [userDetails]);

  function convertUTCDateToIsoString(dateStr: string) {
    try {
      if (!dateStr) {
        return new Date(0).toISOString();
      }
      return moment.utc(dateStr, "D MMM YYYY").toISOString();
    } catch (e) {
      return new Date(0).toISOString();
    }
  }

  function totalReviewTasksSubmittedInCurrentCycle() {
    return appsList.reduce((totalTaskCount: number, app) => {
      const recentReview = app.recentReviews?.inProgressReview || app.recentReviews?.lastClosedReview;
      if (recentReview) {
        const reviewCycleDate = parseDate(currentReviewCycleStartDate.split("T")[0]).toISOString();
        const reviewStartDate = convertUTCDateToIsoString(recentReview.startDate);
        if (reviewStartDate >= reviewCycleDate) {
          const updatedReview = findReviewById(recentReview.id);
          if (updatedReview) {
            return totalTaskCount + (getSubmittedTasksCountForUser(updatedReview));
          }
          return totalTaskCount + (recentReview.noOfTasksSubmittedByTheUser ?? 0);
        }
      }
      return totalTaskCount;
    }, 0);
  }

  useEffect(() => {
    /* eslint-disable no-underscore-dangle */
    const qualarooSurvey = window._kiq = window._kiq || [];
    iif(function setCloseEventHandler() {
      qualarooSurvey.push(["eventHandler", "submit", function(_answers_array, _nudge_id, screen_id) {
        if(screen_id === config.surveySubmitScreenId) {
          localStorage.setItem("survey-submitted-time", new Date().toISOString());
          setVisible(false);
        }
      }]);
    });
    if (appsList && currentReviewCycleStartDate) {
      const totalReviewTasksSubmittedInTheCurrentCycle = totalReviewTasksSubmittedInCurrentCycle();
      qualarooSurvey.push([
        "set",
        {
          visible,
          reviewTaskSubmittedCount: totalReviewTasksSubmittedInTheCurrentCycle,
          reviewCycleStartDate: new Date(currentReviewCycleStartDate).toUTCString(),
        },
      ]);
    }
  }, [applications, reviews]);
}
